import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import ShareButtons from '../components/ShareButtons';
import { ArrowLeft, MapPin, Phone, Mail, User, Tag, Calendar, ChevronLeft, ChevronRight, CheckCircle, Clock, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LostFoundDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const editToken = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/lost-found/${id}`)
      .then(res => setItem(res.data))
      .catch(() => navigate('/classifieds'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleResolve = async () => {
    if (!editToken) return;
    setResolving(true);
    try {
      await axios.put(`${API}/api/lost-found/${id}/resolved?token=${editToken}`);
      setItem(prev => ({ ...prev, status: 'resolved' }));
    } catch { alert(t('lostFound.errorResolve', 'Could not mark as resolved.')); }
    finally { setResolving(false); }
  };

  const handleDelete = async () => {
    if (!editToken) return;
    setDeleting(true);
    try { await axios.delete(`${API}/api/lost-found/${id}?token=${editToken}`); navigate('/classifieds'); }
    catch { alert(t('classifieds.errorDelete', 'Could not delete.')); }
    finally { setDeleting(false); }
  };

  const categoryIcons = { 'Pets': '🐾', 'Keys': '🔑', 'Wallets/Bags': '👜', 'Documents/ID': '📄', 'Phones/Electronics': '📱', 'Other': '📦' };

  /**
   * Iter 160 — Build a wa.me link for the Lost & Found contact phone.
   * Strips spaces, brackets, dashes, dots, and a leading +. If the number is
   * a clearly Spanish mobile (starts with 6 or 7 after cleaning) and has no
   * country code, default it to Spain (34). Returns null if no usable digits.
   */
  const buildWhatsAppHref = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    // Keep only digits — drops +, spaces, brackets, dashes, dots, slashes, etc.
    let digits = raw.replace(/\D+/g, '');
    if (!digits) return null;
    // If the number looks like a national-only Spanish mobile (9 digits starting
    // with 6 or 7), prefix the Spain country code (34). Numbers that already
    // include 34… or any other longer country code pass through unchanged.
    if (digits.length === 9 && (digits[0] === '6' || digits[0] === '7')) {
      digits = '34' + digits;
    }
    const message = t(
      'lostFound.whatsappMessage',
      'Hi, I saw your Lost & Found post on Find-2-Day about {{title}}. Is it still available / can I collect it?',
      { title: item?.title || '' }
    );
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="animate-pulse max-w-2xl mx-auto px-4 pt-6"><div className="h-64 bg-gray-200 rounded-xl mb-4"></div></div>
      <BottomNavigation />
    </div>
  );
  if (!item) return null;

  const photos = item.photos || [];
  const isResolved = item.status === 'resolved';
  const isLost = item.itemType === 'lost';

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/classifieds')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900" data-testid="back-from-lf-detail">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">{t('lostFound.title', 'Lost & Found')}</span>
          </button>
          <ShareButtons url={window.location.href} title={item.title} size="sm" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Type badge + Status */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${isLost ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
            {isLost ? t('lostFound.lost', 'Lost') : t('lostFound.found', 'Found')}
          </span>
          {isResolved && (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase" data-testid="resolved-badge">
              {t('lostFound.resolved', 'Resolved')}
            </span>
          )}
        </div>

        {/* Photo gallery */}
        {photos.length > 0 ? (
          <div className="relative rounded-xl overflow-hidden bg-gray-100 mb-4">
            <img src={photos[currentPhoto]} alt={item.title} className="w-full aspect-[4/3] object-cover" data-testid="lf-main-photo" />
            {isResolved && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="bg-green-500 text-white px-6 py-2 rounded-full text-lg font-bold uppercase">{t('lostFound.resolved', 'Resolved')}</span>
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button onClick={() => setCurrentPhoto(p => p > 0 ? p - 1 : photos.length - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => setCurrentPhoto(p => p < photos.length - 1 ? p + 1 : 0)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 flex items-center justify-center aspect-[16/10] mb-4">
            <span className="text-6xl">{categoryIcons[item.category] || '📦'}</span>
          </div>
        )}

        <h1 className="text-xl font-semibold text-gray-900 mb-3" data-testid="lf-detail-title">{item.title}</h1>

        <div className="flex items-center gap-3 mb-4 text-sm text-gray-500 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <Tag className="h-3 w-3" /> {t(`lostFound.cat.${item.category}`, item.category)}
          </span>
          {item.date && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {item.date}</span>
          )}
          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(item.createdAt)}</span>
        </div>

        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('classifieds.description', 'Description')}</h2>
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed" data-testid="lf-detail-desc">{item.description}</p>
        </div>

        {item.location && (
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm" data-testid="lf-detail-location">{item.location}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('lostFound.contactPerson', 'Contact')}</h2>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm text-gray-700"><User className="h-4 w-4 text-blue-600 flex-shrink-0" /><span>{item.contactName}</span></div>
            <a href={`mailto:${item.contactEmail}`} className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800"><Mail className="h-4 w-4 flex-shrink-0" />{item.contactEmail}</a>
            {item.contactPhone && <a href={`tel:${item.contactPhone}`} className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800" data-testid="lf-contact-phone"><Phone className="h-4 w-4 flex-shrink-0" />{item.contactPhone}</a>}
            {item.contactPhone && buildWhatsAppHref(item.contactPhone) && (
              <a
                href={buildWhatsAppHref(item.contactPhone)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 mt-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors w-full sm:w-auto"
                data-testid="lf-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {t('lostFound.messageOnWhatsApp', 'Message on WhatsApp')}
              </a>
            )}
          </div>
        </div>

        {/* Owner actions */}
        {editToken && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-blue-700 mb-3">{t('classifieds.ownerActions', 'You have owner access to this listing.')}</p>
            <div className="flex gap-2">
              {item.status === 'active' && (
                <button onClick={handleResolve} disabled={resolving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 text-sm"
                  data-testid="resolve-btn">
                  <CheckCircle className="h-4 w-4" />
                  {resolving ? '...' : t('lostFound.markResolved', 'Mark as Resolved')}
                </button>
              )}
              {item.status === 'resolved' && (
                <p className="flex-1 text-sm text-green-700">{t('lostFound.resolvedDesc', 'This item has been marked as resolved.')}</p>
              )}
              <button onClick={() => setShowConfirmDelete(true)}
                className="px-4 py-2.5 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 text-sm"
                data-testid="lf-delete-btn">
                {t('classifieds.delete', 'Delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('classifieds.confirmDelete', 'Delete Listing?')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('classifieds.confirmDeleteDesc', 'This will permanently remove your listing.')}</p>
            <div className="flex gap-2">
              <button onClick={() => setShowConfirmDelete(false)} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 text-sm">{t('cancel', 'Cancel')}</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 text-sm" data-testid="lf-confirm-delete">{deleting ? '...' : t('classifieds.yesDelete', 'Yes, Delete')}</button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default LostFoundDetail;
